import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { PortalSpinner } from 'components/PortalSpinner';
import Stepper from 'components/Profile/Stepper';
import { ProfileActions } from 'store/actions';
import { PORTAL_ROUTES } from 'utils/constants';
import { getInsurance, getProfileCompletion, getStepById, getNextStep, stepRoute } from './helper';

const enhancer = connect(null, { ...ProfileActions });

const SECTION_KEY = {
  personal: 'personal_information',
  insurance: 'insurance_intake',
  medical: 'medical_history',
  hipaa: 'hipaa_forms',
  consent: 'consent_forms',
  treatment: 'upcoming_treatments_consent_forms',
};

// `embedded`: the /portal/forms/:step route used by the mobile app WebView. One form only:
// no stepper, no step-to-step navigation; the footers read it from the outlet context.
const ProfileLayout = ({ showProfile, embedded = false }) => {
  const [profile, setProfile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const refetchOnEnter = ['hipaa', 'consent'].includes(lastSegment);

  useEffect(() => {
    if (refetchOnEnter || !profile) fetchProfile();
  }, [lastSegment]);

  async function fetchProfile() {
    try {
      const { data } = await showProfile();
      setProfile(data);
    } catch (error: any) {
      toast.error(error.errors || 'Failed to load profile. Please try again later.');
    }
  }

  const mergeSection = (id, resource) =>
    setProfile((p) => ({ ...p, attributes: { ...p.attributes, [SECTION_KEY[id]]: { data: resource } } }));

  const attributes = profile?.attributes;
  const completion = getProfileCompletion(attributes);

  const sectionData = {
    personal: attributes?.personal_information?.data?.attributes,
    insurance: getInsurance(attributes),
    medical: attributes?.medical_history?.data?.attributes,
    hipaa: attributes?.hipaa_forms?.data,
    consent: attributes?.consent_forms?.data,
    treatmentConsents: attributes?.upcoming_treatments_consent_forms?.data,
    contacts: {
      authorized: attributes?.authorized_contacts?.data,
      revoked: attributes?.revoked_contacts?.data,
      family_members: attributes?.family_members?.data,
      emergency_contacts: attributes?.emergency_contacts?.data,
    },
    practice: attributes?.practice?.data?.attributes
  };

  const activeStep = getStepById(lastSegment);

  const goToStep = (step) => navigate(stepRoute(step));

  const goToNextStep = (currentId) => {
    if (embedded) return; // stay on the form, the saved state re-renders in place

    const next = getNextStep(currentId);
    navigate(next ? stepRoute(next) : PORTAL_ROUTES.PROFILE);
  };

  if (!profile) return <PortalSpinner />;

  return (
    <div className={embedded ? 'relative h-screen supports-[height:100dvh]:h-dvh bg-white' : 'relative -m-5 flex-1 lg:-m-6'}>
      <div className="absolute inset-0 flex flex-col overflow-hidden">
        {activeStep && !embedded && (
          <Stepper completion={completion} activeId={activeStep.id} onStepClick={goToStep} />
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet context={{ sectionData, completion, mergeSection, goToNextStep, embedded, refreshProfile: fetchProfile }} />
        </div>
      </div>
    </div>
  );
};

export default enhancer(ProfileLayout);
